import { useEffect, useState } from "react";
import styles from "./MarketPlaceDetail.module.css";
import { CircularProgress, MenuItem, Select, Button } from "@mui/material";
import MarketPlaceT1 from "./MarketPlaceT1";
import MarketPlaceT2 from "./MarketPlaceT2";
import MarketPlaceT3 from "./MarketPlaceT3";
import MarketPlaceRequiredField from "../MarketPlacePricing/MarketPlaceRequiredField";
import { BsArrowLeftCircle } from "react-icons/bs";
import { FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import { pinnedSkipMessage } from "../../../utils/stripeSyncPinned";
import SetDefault from "../MarketPlaceTabbed/SetDefault";
import EndUser from "../MarketPlaceTabbed/EndUser";
import ProductOwnership from "./ProductOwnership";
import SubmitConfirmation from "../../AdminProducts/MarketPlaceDetail/SubmitConfirmation";

function MarketPlaceDetails({ selectedMarketPlace, setSelectedMarketPlace }) {
  const [paymentStatus, setPaymentStatus] = useState("");
  const [activeStatus, setActiveStatus] = useState("Active");
  const [planType, setPlanType] = useState([
    {
      skuId: "",
      name: "Default",
      parentId: "",
      levels: "",
      template: "recurring",
      required_fields: [],
      allow_delete: true,
    },
  ]);
  const [selected, setSelected] = useState(0);
  const [intervals, setIntervals] = useState([]);
  const [selectInterval, setSelectedInterval] = useState(0);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [vendors, setVendors] = useState([]);
  const [activeTab, setActiveTab] = useState("prices");
  const [submitconfirmation, setSubmitConfirmation] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [isSomethingChanged, setIsSomethingChanged] = useState(false);

  // Show Required Model state
  const [showRequiredFieldModal, setShowRequiredFieldModal] = useState(false);

  useEffect(() => {
    try {
      async function fetchData() {
        setFetchLoader(true);
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/admin/get_all_marketplace_pricing`,
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ skuId: selectedMarketPlace.id }),
          },
        );
        const data = await res.json();
        setFetchLoader(false);
        if (data.status === 200) {
          setPlanType(data.planType);
          const intervalsWithIndex = data.intervals.map((int, index) => ({
            ...int,
            index,
          }));

          setIntervals(intervalsWithIndex);
          setPlans(
            data.plans.map((p) => {
              const intv = intervalsWithIndex[p.interval] || {};

              return {
                ...p,
                n_price_validity_interval:
                  p.n_price_validity_interval ?? intv.n_interval ?? "",
                price_validity_interval:
                  p.price_validity_interval ?? intv.interval ?? "",
              };
            }),
          );
          setFeatures(data.features);
          if (!data.planType.length) {
            setPlanType([
              {
                skuId: "",
                name: "Default",
                parentId: "",
                levels: "",
                template: "recurring",
                required_fields: [],
                allow_delete: true,
              },
            ]);
          }
          setVendors(data.vendors);
          if (data.vendors.length === 1)
            setSelectedVendor(data.vendors[0].vendor_id);
          setSelectedInterval(0);
        } else {
          toast.error("Something went wrong", "error");
        }
      }
      fetchData();
    } catch (error) {
      console.log("Error", error);
    }
    // eslint-disable-next-line
  }, [isSomethingChanged]);

  useEffect(() => {
    const first = intervals.find((i) => i.typeId === selected);
    setSelectedInterval(first ? first.index : null);
    // eslint-disable-next-line
  }, [selected]);

  //REQUIRED FIELD MODAL HANDLERS:
  const requiredFieldOpenHandler = () => {
    setShowRequiredFieldModal(true);
  };

  const requiredFieldCloseHandler = () => {
    setShowRequiredFieldModal(false);
  };

  const filteredFeaturesData = features.filter((f) => f.typeId === selected);

  const filteredAppsData = plans.filter(
    (p) =>
      p.typeId === selected &&
      p.interval === selectInterval &&
      (activeStatus === "" ||
        (activeStatus === "Active" && p.status === "Active") ||
        (activeStatus === "Inactive" && p.status === "Inactive") ||
        (activeStatus === "Out Of Stock" && p.status === "Out Of Stock")) &&
      (paymentStatus === "" ||
        (paymentStatus === "GL" && p.region === "GL") ||
        (paymentStatus === "US" && p.region === "US") ||
        (paymentStatus === "IN" && p.region === "IN")),
  );

  const handleSave = async () => {
    try {
      setSubmitLoader(true);
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/update_mp_price`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            plans,
            skuId: selectedMarketPlace.id,
          }),
        },
      );
      const data = await res.json();
      setSubmitLoader(false);
      if (data.status === 200) {
        setSubmitConfirmation(false);
        const sync = data.stripe_sync;
        if (sync && sync.ok === false) {
          const n =
            sync.failed ??
            (Array.isArray(sync.failures) ? sync.failures.length : null);
          toast.warn(
            `Prices saved but Stripe sync failed${
              n ? ` for ${n} subscription${n === 1 ? "" : "s"}` : ""
            } — check subscription price update queue`,
          );
        } else {
          toast.success("Saved Successfully");
        }
        const pinnedMsg = pinnedSkipMessage(sync);
        if (pinnedMsg) toast.warn(pinnedMsg, { autoClose: 9000 });
        setIsSomethingChanged(!isSomethingChanged);
      } else {
        toast.error("Failed to save, try again");
      }
    } catch (error) {
      console.log("Error", error);
      toast.error("Failed to save, try again");
    }
  };

  return (
    <div>
      <div className={styles.topHeader}>
        <div
          onClick={() => setSelectedMarketPlace({})}
          className={styles.backButton}
        >
          <BsArrowLeftCircle
            style={{ fontSize: "24px", borderRadius: "50%" }}
          />
        </div>
        <h1 className={styles.marketPlaceHeading}>Market Place Prices</h1>
      </div>
      {/* Who governs the plan NAMES on this product — and the one button that
          changes it. Nothing here moves a seller, a price or a subscription. */}
      <ProductOwnership
        product={selectedMarketPlace}
        onChanged={(next) => setSelectedMarketPlace(next)}
      />
      {/* Tabbed Container */}
      <div className={styles.TabbedContainer}>
        <div className={styles.tabHeader}>
          <h1
            className={`${styles.tab} ${
              activeTab === "prices" ? styles.activeTab : ""
            }`}
            onClick={() => setActiveTab("prices")}
          >
            Prices
          </h1>
          <h1
            className={`${styles.tab} ${
              activeTab === "endUser" ? styles.activeTab : ""
            }`}
            onClick={() => setActiveTab("endUser")}
          >
            End user View
          </h1>
          <h1
            className={`${styles.tab} ${
              activeTab === "default" ? styles.activeTab : ""
            }`}
            onClick={() => setActiveTab("default")}
          >
            Set Default
          </h1>
        </div>
      </div>

      {activeTab === "prices" && (
        <div className={styles.Container}>
          {/* Filter Container  */}
          <div className={styles.topContainer}>
            <div className={styles.back_filter}>
              <div className={styles.topRightContainer}>
                <div className={styles.filterBox}>
                  <Select
                    value={selectedVendor}
                    onChange={(e) => setSelectedVendor(e.target.value)}
                    displayEmpty
                    sx={{ width: "25%" }}
                  >
                    <MenuItem key={0} value={""}>
                      --select--
                    </MenuItem>
                    {vendors.map((v) => (
                      <MenuItem key={v.id} value={v.vendor_id}>
                        {v.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    value={activeStatus}
                    onChange={(e) => setActiveStatus(e.target.value)}
                    displayEmpty
                    sx={{ width: "25%" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                    <MenuItem value="Out Of Stock">Out of Stock</MenuItem>
                  </Select>
                  <Select
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                    displayEmpty
                    sx={{ width: "25%" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="GL">GLOBAL</MenuItem>
                    <MenuItem value="US">US</MenuItem>
                    <MenuItem value="IN">IN</MenuItem>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          {/* Bottom T1 T2 T3 Container */}
          <div>
            {fetchLoader && (
              <div className={styles.fetchLoader}>
                <CircularProgress size={25} />
              </div>
            )}
            {!fetchLoader && selectedVendor !== "" && (
              <div className={styles.T1Container}>
                <div className={styles.plan_interval}>
                  {/* Plan Type  */}
                  <div className={styles.planTypecontainer}>
                    <div className={styles.typeheading}>Add Plan Type</div>
                    <ul className={styles.plan_list}>
                      {planType.map((type, i) => (
                        <li
                          onClick={() => {
                            setSelected(i);
                          }}
                          key={i}
                          className={`${styles.plan_item} ${selected === i ? styles.selectedCSS : ""}`}
                        >
                          {type.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {/* Interval */}
                  <div className={styles.intervalContainer}>
                    <div className={styles.intervalheading}>Add Intervals</div>
                    <ul className={styles.interval_list}>
                      {intervals
                        .map((interval, index) => ({ ...interval, index }))
                        .filter((int) => int.typeId === selected)
                        .map((interval, i) => (
                          <li
                            key={interval.index}
                            className={`${styles.plan_item} ${
                              selectInterval === interval.index
                                ? styles.selectedCSS
                                : ""
                            }`}
                            onClick={() => setSelectedInterval(interval.index)}
                          >
                            {interval.n_interval} {interval.interval}
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
                {/* Required (fix)  */}
                <div className={styles.formGroup}>
                  <label className={styles.RequiredTitle}>
                    Required Fields
                  </label>
                  <Button
                    className={styles.addFieldButton}
                    onClick={requiredFieldOpenHandler}
                  >
                    <FaPlus />
                  </Button>
                  {showRequiredFieldModal && (
                    <MarketPlaceRequiredField
                      jsonData={planType[selected].required_fields}
                      requiredFieldCloseHandler={requiredFieldCloseHandler}
                    />
                  )}
                </div>
                {/* Plan Data */}
                <div className={styles.horizontalForm}>
                  {planType[selected]?.template === "recurring" && (
                    <MarketPlaceT1
                      selected={selected}
                      selectInterval={selectInterval}
                      plans={filteredAppsData}
                      setPlans={setPlans}
                      features={filteredFeaturesData}
                      selectedVendor={selectedVendor}
                    />
                  )}
                  {planType[selected]?.template === "credit" && (
                    <MarketPlaceT2
                      selected={selected}
                      selectInterval={selectInterval}
                      plans={filteredAppsData}
                      setPlans={setPlans}
                      features={filteredFeaturesData}
                      selectedVendor={selectedVendor}
                    />
                  )}
                  {planType[selected]?.template === "one_time" && (
                    <MarketPlaceT3
                      selected={selected}
                      selectInterval={selectInterval}
                      plans={filteredAppsData}
                      setPlans={setPlans}
                      features={filteredFeaturesData}
                      selectedVendor={selectedVendor}
                    />
                  )}
                </div>
                <div style={{ textAlign: "center" }}>
                  <button
                    className={styles.submitBtn}
                    onClick={() => {
                      setSubmitConfirmation(true);
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
            {submitconfirmation && (
              <SubmitConfirmation
                handleClose={() => setSubmitConfirmation(false)}
                handleSave={handleSave}
                submitLoader={submitLoader}
              />
            )}
            {!fetchLoader && selectedVendor === "" && (
              <p className={styles.selectVendor}>
                Select Vendor to see there prices
              </p>
            )}
          </div>
        </div>
      )}

      {activeTab === "endUser" && (
        <div>
          <EndUser skuId={selectedMarketPlace.id} />
        </div>
      )}

      {activeTab === "default" && (
        <SetDefault
          skuId={selectedMarketPlace.id}
          onSaved={() => setIsSomethingChanged((prev) => !prev)}
        />
      )}
    </div>
  );
}

export default MarketPlaceDetails;
